import { t as tCem } from '../utils/i18n'
/**
 * SiteConfigView — Sprint A2.
 *
 * Plano de la tienda + cámaras posicionadas + drawer lateral con
 * tabs de configuración por analítica.
 *
 * UX core:
 *   1. Selector de sitio arriba.
 *   2. Si el sitio no tiene plano, botón "Subir plano".
 *   3. Si tiene plano, Konva canvas lo muestra escalado a viewport.
 *   4. Cámaras del sitio aparecen como íconos sobre el plano. Las que
 *      no tienen posición van en una fila debajo del canvas listas
 *      para arrastrar al plano.
 *   5. Drag-end de cámara → POST /api/cameras/{id}/position con x_pct/y_pct.
 *   6. Click en cámara → drawer lateral con tabs (General + analíticas).
 *   7. Cambios en drawer → PUT /api/cameras/{id} (backend auto-regenera
 *      remote_config para que el daemon edge lo tome en su próximo heartbeat).
 *
 * Accesibilidad (Paulo es daltónico):
 *   - Estado de cámara siempre tiene texto, no solo color
 *   - Cámaras posicionadas tienen ✓ visible
 *   - Activa/inactiva con etiqueta textual
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Stage, Layer, Image as KImage, Circle, Text, Group, Arrow } from 'react-konva'
import { useAuthStore } from '../store/authStore'
import SiteFloorPlanSection from '../components/SiteFloorPlanSection'

// ─── Helpers ───────────────────────────────────────────────────────────────

async function apiFetch(token, path, opts = {}) {
  const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token}` }
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }
  const r = await fetch(path, { ...opts, headers })
  if (!r.ok) {
    let detail = ''
    try { detail = (await r.json()).detail } catch {}
    throw new Error(`HTTP ${r.status}${detail ? ': ' + detail : ''}`)
  }
  return r
}

function useImage(src) {
  const [img, setImg] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!src) { setImg(null); return }
    const im = new window.Image()
    im.crossOrigin = 'anonymous'
    im.onload  = () => setImg(im)
    im.onerror = (e) => { setErr(e); setImg(null) }
    im.src = src
  }, [src])
  return [img, err]
}


// ─── Canvas del plano + íconos cámara ──────────────────────────────────────

function FloorPlanCanvas({
  imageBlobUrl, planMeta, cameras, selectedId,
  onSelectCamera, onMoveCamera, viewportRef,
}) {
  const [img] = useImage(imageBlobUrl)
  const [size, setSize] = useState({ w: 800, h: 600 })

  // Ajustar canvas al tamaño del viewport
  useEffect(() => {
    const upd = () => {
      const el = viewportRef.current
      if (!el) return
      const w = Math.max(400, el.clientWidth - 24)
      // Mantener aspect ratio del plano
      const ar = planMeta ? (planMeta.height_px / planMeta.width_px) : 0.75
      setSize({ w, h: Math.round(w * ar) })
    }
    upd()
    window.addEventListener('resize', upd)
    return () => window.removeEventListener('resize', upd)
  }, [planMeta, viewportRef])

  return (
    <Stage width={size.w} height={size.h} style={{
      background: 'var(--bg-elev-1)',
      border: '1px solid var(--border)',
      borderRadius: 6,
    }}>
      <Layer>
        {img && (
          <KImage image={img} width={size.w} height={size.h} opacity={0.85} />
        )}
        {cameras.filter(c => c.floor_plan?.x_pct != null).map(cam => {
          const x = cam.floor_plan.x_pct * size.w
          const y = cam.floor_plan.y_pct * size.h
          const isSelected = cam.id === selectedId
          const rot = cam.floor_plan.rotation_deg || 0
          // Cono de visión: una flecha que sale del centro hacia rot grados
          const ang = (rot - 90) * Math.PI / 180  // 0deg = up
          const coneLen = 30
          const dx = Math.cos(ang) * coneLen
          const dy = Math.sin(ang) * coneLen
          return (
            <Group
              key={cam.id}
              x={x} y={y}
              draggable
              onClick={() => onSelectCamera(cam.id)}
              onTap={() => onSelectCamera(cam.id)}
              onDragEnd={(e) => {
                const newX = e.target.x()
                const newY = e.target.y()
                onMoveCamera(cam.id, newX / size.w, newY / size.h)
              }}
            >
              {/* Cono de visión */}
              <Arrow
                points={[0, 0, dx, dy]}
                stroke={isSelected ? '#21a366' : '#0091ff'}
                fill={isSelected ? '#21a366' : '#0091ff'}
                pointerLength={6}
                pointerWidth={6}
                opacity={0.5}
              />
              {/* Círculo del ícono */}
              <Circle
                radius={14}
                fill={cam.enabled ? '#0091ff' : '#666'}
                stroke={isSelected ? '#21a366' : '#ffffff'}
                strokeWidth={isSelected ? 3 : 2}
                shadowColor="black" shadowBlur={4} shadowOpacity={0.4}
              />
              {/* Número del canal */}
              <Text
                text={String(cam.channel)}
                x={-7} y={-7}
                fontSize={14} fontStyle="bold"
                fill="white"
                width={14} height={14}
                align="center"
                verticalAlign="middle"
              />
              {/* Label flotante (nombre) — debajo del ícono */}
              <Text
                text={cam.name}
                x={-60} y={18}
                width={120}
                fontSize={11}
                fill={isSelected ? '#21a366' : '#ffffff'}
                align="center"
                shadowColor="black" shadowBlur={3} shadowOpacity={0.9}
              />
            </Group>
          )
        })}
      </Layer>
    </Stage>
  )
}


// ─── Banda con todas las cámaras del sitio ─────────────────────────────────
//
// Muestra TODAS las cámaras (posicionadas y no). Click siempre:
//   1. Selecciona la cámara (abre drawer lateral).
//   2. Si no estaba posicionada, además la coloca en el centro del plano.
//
// Por qué no filtramos solo "sin posicionar": al colocar una cámara
// desaparecía del strip y daba la impresión de que se había perdido.
// Mostrar todas evita ese efecto y deja el strip como atajo permanente.
function CamerasStrip({
  cameras, selectedId, onSelectCamera,
  placingCameraId, onArmPlacement, onClearPlacement,
}) {
  if (cameras.length === 0) return null
  const sorted = [...cameras].sort((a, b) => (a.channel ?? 0) - (b.channel ?? 0))
  const unplacedCount = cameras.filter(c => c.lat == null).length
  const placingCam = placingCameraId
    ? cameras.find(c => c.id === placingCameraId)
    : null
  return (
    <div style={{
      marginTop: 12, padding: 12,
      background: 'var(--bg-elev-1)', border: '1px solid var(--border)',
      borderRadius: 6,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 8,
      }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Cámaras del sitio ({cameras.length})
          {unplacedCount > 0 && ` — ${unplacedCount} sin posicionar`}
        </div>
        {placingCam && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11, color: '#fbbf24', fontWeight: 600,
          }}>
            Colocando <b>CH{placingCam.channel} {placingCam.name}</b> — clickeá sobre el plano
            <button
              onClick={onClearPlacement}
              style={{
                padding: '2px 8px', fontSize: 10,
                background: 'transparent', color: '#fbbf24',
                border: '1px solid #fbbf24', borderRadius: 3, cursor: 'pointer',
              }}
            >
              cancelar
            </button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {sorted.map(cam => {
          const placed = cam.lat != null
          const isSelected = cam.id === selectedId
          const isPlacing = cam.id === placingCameraId
          return (
            <button
              key={cam.id}
              onClick={() => {
                if (!placed) {
                  // Armar para colocar — el próximo click sobre el mapa la posiciona
                  onArmPlacement(cam.id)
                } else {
                  // Ya está en el plano — solo seleccionar + abrir drawer
                  onSelectCamera(cam.id)
                }
              }}
              title={placed
                ? `${cam.name} — en el plano (click abre configuración)`
                : `${cam.name} — sin posición (click arma, luego clickeá el plano)`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', fontSize: 12,
                border: isPlacing
                  ? '2px solid #fbbf24'
                  : isSelected
                    ? '2px solid #0091ff'
                    : '1px solid var(--border)',
                borderRadius: 4,
                background: isPlacing
                  ? 'rgba(251,191,36,0.12)'
                  : isSelected ? 'rgba(0,145,255,0.08)' : 'transparent',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: '50%',
                background: placed ? '#0091ff' : 'var(--bg-elev-2)',
                color: placed ? 'white' : 'var(--text-muted)',
                fontSize: 11, fontWeight: 'bold',
                border: placed ? 'none' : '1px dashed var(--border)',
              }}>
                {cam.channel}
              </span>
              <span style={{ fontWeight: isSelected ? 600 : 400 }}>{cam.name}</span>
              <span style={{
                fontSize: 9, color: 'var(--text-muted)',
                marginLeft: 4, letterSpacing: '0.04em',
              }}>
                {placed ? '·  en plano' : '·  click para colocar'}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}


// ─── Drawer lateral con tabs ───────────────────────────────────────────────

function CameraDrawer({
  camera, siteId, onClose, onUpdate, onUpdatePosition, token,
  onLivePreview,    // (cameraId, partialPatch) — actualizar state padre en vivo
}) {
  const [activeTab, setActiveTab] = useState('general')
  const [draft, setDraft] = useState(camera)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Snapshot del estado original al abrir el drawer. El padre actualiza
  // `camera` cada vez que el live preview cambia el state global de cameras,
  // así que `JSON.stringify(draft) !== JSON.stringify(camera)` siempre daba
  // false → botón "Sin cambios" → POST nunca se hacía. Comparamos contra
  // este snapshot inicial para que el dirty refleje cambios reales.
  const initialCamRef = useRef(camera)
  useEffect(() => {
    setDraft(camera)
    initialCamRef.current = camera
  }, [camera?.id])

  // Live preview: cuando cambian los valores del cono en el draft,
  // propagar al state global para que el cono en el plano se redibuje
  // en vivo sin tener que guardar.
  useEffect(() => {
    if (!onLivePreview || !camera) return
    const patch = {
      fov_horizontal_deg: draft.fov_horizontal_deg,
      effective_range_m : draft.effective_range_m,
      floor_plan: draft.floor_plan,    // incluye rotation_deg actual
    }
    onLivePreview(camera.id, patch)
    // intencionalmente sin onLivePreview en deps para evitar loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draft.fov_horizontal_deg,
    draft.effective_range_m,
    draft.floor_plan?.rotation_deg,
    camera?.id,
  ])

  if (!camera) return null

  const dirty = JSON.stringify(draft) !== JSON.stringify(initialCamRef.current)

  const handleSave = async () => {
    setSaving(true); setErr(null)
    try {
      // Separar 3 grupos: floor_plan (position), cono (rotation+fov+range), resto (PUT).
      const changes = {}
      let positionChanged = false
      let coneChanged = false
      const CONE_KEYS = new Set(['fov_horizontal_deg', 'effective_range_m'])
      for (const k of Object.keys(draft)) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(initialCamRef.current[k])) {
          if (k === 'floor_plan') positionChanged = true
          else if (CONE_KEYS.has(k)) coneChanged = true
          else changes[k] = draft[k]
        }
      }
      // Rotación vive dentro de floor_plan en el draft pero también es parte del
      // cono. Detectar cambio de rotación específicamente.
      const rotChanged = (draft.floor_plan?.rotation_deg ?? 0) !== (initialCamRef.current.floor_plan?.rotation_deg ?? 0)
      if (rotChanged) coneChanged = true

      // PUT con cambios generales (analytics, name, etc.)
      if (Object.keys(changes).length > 0) {
        await onUpdate(camera.id, changes)
      }
      // POST /position si cambió x_pct/y_pct
      if (positionChanged && draft.floor_plan?.x_pct != null) {
        await onUpdatePosition(camera.id, {
          x_pct: draft.floor_plan.x_pct,
          y_pct: draft.floor_plan.y_pct,
          rotation_deg: draft.floor_plan.rotation_deg ?? 0,
        })
      }
      // POST /cone si cambió FoV, alcance o rotación
      if (coneChanged) {
        const r = await fetch(`/api/cameras/${camera.id}/cone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            rotation_deg      : draft.floor_plan?.rotation_deg ?? 0,
            fov_horizontal_deg: draft.fov_horizontal_deg ?? 70,
            effective_range_m : draft.effective_range_m  ?? 15,
          }),
        })
        if (!r.ok) {
          const d = await r.json().catch(() => ({}))
          throw new Error(d.detail || `HTTP ${r.status}`)
        }
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setSaving(false)
    }
  }

  const TABS = [
    { id: 'general',   label: 'General' },
    { id: 'person',    label: 'Persona' },
    { id: 'vehicle',   label: 'Vehículo' },
    { id: 'loitering', label: 'Merodeo' },
    { id: 'line',      label: 'Cruce línea' },
    { id: 'shadow',    label: 'Calibración' },
    { id: 'schedule',  label: tCem('Horario','Schedule') },
    { id: 'alerts',    label: tCem('Alertas','Alerts') },
    { id: 'history',   label: 'Historial' },
  ]

  const has = (kind) => draft.analytics?.includes(kind) || false
  const toggle = (kind) => {
    const a = new Set(draft.analytics || [])
    if (a.has(kind)) a.delete(kind); else a.add(kind)
    setDraft({ ...draft, analytics: [...a] })
  }

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0,
      width: 420, background: 'var(--bg-surface)',
      borderLeft: '1px solid var(--border)',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.3)',
      display: 'flex', flexDirection: 'column',
      zIndex: 50,
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            CH{String(camera.channel).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {camera.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Estado: {camera.enabled ? '[ACTIVA]' : '[DESHABILITADA]'}
            {camera.floor_plan?.x_pct != null && ' · Posicionada en plano'}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--text-primary)', padding: '4px 10px',
            borderRadius: 4, cursor: 'pointer', fontSize: 14,
          }}
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              flex: 1, padding: '10px 4px', fontSize: 12,
              border: 'none', background: 'transparent',
              color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
              borderBottom: activeTab === t.id ? '2px solid #0091ff' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === t.id ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {activeTab === 'general' && (
          <TabGeneral draft={draft} setDraft={setDraft} />
        )}
        {activeTab === 'person' && (
          <TabPerson draft={draft} setDraft={setDraft} has={has} toggle={toggle} />
        )}
        {activeTab === 'vehicle' && (
          <TabVehicle draft={draft} setDraft={setDraft} has={has} toggle={toggle} />
        )}
        {activeTab === 'loitering' && (
          <TabLoitering draft={draft} setDraft={setDraft} />
        )}
        {activeTab === 'line' && (
          <TabLine draft={draft} setDraft={setDraft} camera={camera} token={token} />
        )}
        {activeTab === 'shadow' && (
          <TabShadow draft={draft} setDraft={setDraft} camera={camera} token={token} />
        )}
        {activeTab === 'schedule' && (
          <TabSchedule draft={draft} setDraft={setDraft} />
        )}
        {activeTab === 'alerts' && (
          <TabAlerts camera={camera} siteId={siteId} token={token} />
        )}
        {activeTab === 'history' && (
          <TabHistory camera={camera} token={token} />
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: 12, borderTop: '1px solid var(--border)',
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        {err && (
          <div style={{
            flex: 1, fontSize: 11, color: '#d93025',
            padding: '4px 8px', background: 'rgba(217,48,37,0.1)',
            borderRadius: 4,
          }}>
            Error: {err}
          </div>
        )}
        <button
          onClick={() => setDraft(camera)}
          disabled={!dirty || saving}
          style={{
            padding: '8px 14px', fontSize: 12,
            border: '1px solid var(--border)', background: 'transparent',
            color: 'var(--text-primary)', borderRadius: 4,
            cursor: dirty && !saving ? 'pointer' : 'default',
            opacity: dirty && !saving ? 1 : 0.4,
          }}
        >
          Descartar
        </button>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          style={{
            padding: '8px 16px', fontSize: 12, fontWeight: 600,
            border: 'none', background: dirty ? '#21a366' : 'var(--border)',
            color: 'white', borderRadius: 4,
            cursor: dirty && !saving ? 'pointer' : 'default',
            opacity: dirty && !saving ? 1 : 0.6,
          }}
        >
          {saving ? tCem('Guardando...','Saving...') : (dirty ? tCem('Guardar y publicar al edge','Save and publish to edge') : tCem('Sin cambios','No changes'))}
        </button>
      </div>
    </div>
  )
}

// ─── Sub-tabs ─────────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block', fontSize: 11, marginBottom: 4,
        color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3,
      }}>
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function NumberInput({ value, onChange, min, max, step, placeholder }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      placeholder={placeholder}
      min={min} max={max} step={step}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13,
        background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
        border: '1px solid var(--border)', borderRadius: 4,
      }}
    />
  )
}

function CheckRow({ label, checked, onChange, hint }) {
  return (
    <label style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      padding: '10px 12px', marginBottom: 6,
      background: checked ? 'rgba(33,164,102,0.08)' : 'var(--bg-elev-1)',
      border: `1px solid ${checked ? '#21a366' : 'var(--border)'}`,
      borderRadius: 4, cursor: 'pointer',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 2 }}
      />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          {label} {checked ? '[ACTIVADA]' : '[desactivada]'}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {hint}
          </div>
        )}
      </div>
    </label>
  )
}

// ─── Presets por tipo de cámara ───────────────────────────────────────────
// 5 presets que dejan ~80% configurado. El operador después afina.
// Cada preset es un PARCHE sobre draft — solo cambia los campos listados.
const PRESETS = {
  perimetral: {
    label: 'Perimetral exterior',
    hint: 'Cámaras de muros y vallas. Loitering nocturno + alertas humanas en horario sensible.',
    patch: {
      fps_analytics: 5,
      analytics: ['person'],
      person_alert: false,
      face_recognition: false,
      weapon_detection: false,
      make_recognition: false,
      loitering_seconds: 30,
      loitering_max_movement_px: 60,
      protected_line: null,
    },
  },
  lpr: {
    label: 'Patente vehicular (LPR)',
    hint: 'Cámaras dedicadas a leer patentes. 15 fps y marca con CLIP.',
    patch: {
      fps_analytics: 15,
      analytics: ['lpr', 'vehicle'],
      make_recognition: true,
      person_alert: false,
      face_recognition: false,
      weapon_detection: false,
      loitering_seconds: null,
      loitering_max_movement_px: null,
      protected_line: null,
    },
  },
  patio: {
    label: 'Patio interno EDS',
    hint: 'Áreas internas con tránsito mixto persona+vehículo, sin reglas estrictas.',
    patch: {
      fps_analytics: 5,
      analytics: ['person', 'vehicle'],
      person_alert: false,
      face_recognition: false,
      weapon_detection: false,
      make_recognition: false,
      loitering_seconds: null,
      loitering_max_movement_px: null,
      protected_line: null,
    },
  },
  caja: {
    label: 'Caja registradora',
    hint: 'Persona en zona de caja. Alerta toda detección, detección de arma activa.',
    patch: {
      fps_analytics: 7,
      analytics: ['person'],
      person_alert: true,
      face_recognition: false,
      weapon_detection: true,
      make_recognition: false,
      loitering_seconds: null,
      loitering_max_movement_px: null,
      protected_line: null,
    },
  },
  retail: {
    label: 'Pasillo retail',
    hint: 'Tráfico de clientes sin alertas activas — datos para heat map y analítica de comportamiento.',
    patch: {
      fps_analytics: 3,
      analytics: ['person'],
      person_alert: false,
      face_recognition: false,
      weapon_detection: false,
      make_recognition: false,
      loitering_seconds: null,
      loitering_max_movement_px: null,
      protected_line: null,
    },
  },
}

function PresetButton({ onApply }) {
  const [open, setOpen] = useState(false)
  return (
    <Field label={tCem("Aplicar preset","Apply preset")} hint={tCem("Configura los 5 tipos típicos en 1 click. Después podés afinar campos individuales.","Configure the 5 typical types in 1 click. You can fine-tune individual fields afterwards.")}>
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 500,
            border: '1px solid var(--border)', borderRadius: 4,
            background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          {open ? 'Cerrar lista' : 'Elegir preset...'}
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            marginTop: 4,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            {Object.entries(PRESETS).map(([key, p]) => (
              <button
                key={key}
                onClick={() => {
                  onApply(p.patch)
                  setOpen(false)
                }}
                style={{
                  display: 'block', width: '100%', padding: '10px 12px',
                  fontSize: 12, border: 'none', background: 'transparent',
                  color: 'var(--text-primary)', cursor: 'pointer',
                  textAlign: 'left', borderBottom: '1px solid var(--border)',
                }}
              >
                <div style={{ fontWeight: 600 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {p.hint}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  )
}

function TabGeneral({ draft, setDraft }) {
  const applyPreset = (patch) => setDraft(d => ({ ...d, ...patch }))
  return (
    <div>
      <PresetButton onApply={applyPreset} />
      <Field label="Nombre">
        <input
          type="text" value={draft.name || ''}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13,
            background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
          }}
        />
      </Field>
      <Field label="Zona (opcional)">
        <input
          type="text" value={draft.zone || ''}
          onChange={e => setDraft({ ...draft, zone: e.target.value || null })}
          placeholder="ej. perímetro, patio, caja, LPR"
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13,
            background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
          }}
        />
      </Field>
      <Field label="FPS para analíticas" hint="Frames por segundo analizados. Más alto = mejor detección, más CPU. Recomendado 3-7.">
        <NumberInput
          value={draft.fps_analytics} min={1} max={30} step={1}
          onChange={v => setDraft({ ...draft, fps_analytics: v ?? 5 })}
        />
      </Field>
      <Field label="Rotación del cono de visión (grados)" hint="Indica hacia dónde apunta la cámara en el plano. 0 = norte, 90 = este, 180 = sur, 270 = oeste.">
        <input
          type="range"
          min={0} max={359} step={5}
          value={draft.floor_plan?.rotation_deg ?? 0}
          onChange={e => setDraft({
            ...draft,
            floor_plan: {
              ...(draft.floor_plan || {}),
              rotation_deg: Number(e.target.value),
            },
          })}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
          {draft.floor_plan?.rotation_deg ?? 0}° ({
            ((draft.floor_plan?.rotation_deg ?? 0) < 45 || (draft.floor_plan?.rotation_deg ?? 0) >= 315) ? 'norte' :
            (draft.floor_plan?.rotation_deg ?? 0) < 135 ? 'este' :
            (draft.floor_plan?.rotation_deg ?? 0) < 225 ? 'sur' :
            'oeste'
          })
        </div>
      </Field>
      <Field label="Apertura del cono (FoV horizontal, grados)" hint="Qué tan amplio es el campo de visión de la cámara. Típico bullet/dome: 70°. Fisheye: 110-180°. Tele/PTZ zoom: 20-40°.">
        <input
          type="range"
          min={10} max={180} step={1}
          value={draft.fov_horizontal_deg ?? 70}
          onChange={e => setDraft({ ...draft, fov_horizontal_deg: Number(e.target.value) })}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
          {draft.fov_horizontal_deg ?? 70}°
        </div>
      </Field>
      <Field label="Alcance efectivo (metros)" hint="Distancia útil de detección. Determina qué tan largo se dibuja el cono sobre el plano y se usa para el reporte diario de heatmap.">
        <input
          type="range"
          min={1} max={100} step={1}
          value={draft.effective_range_m ?? 15}
          onChange={e => setDraft({ ...draft, effective_range_m: Number(e.target.value) })}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
          {draft.effective_range_m ?? 15} m
        </div>
      </Field>
      <CheckRow
        label="Cámara habilitada"
        checked={draft.enabled}
        onChange={v => setDraft({ ...draft, enabled: v })}
        hint="Si está desactivada, el daemon edge deja de capturar y analizar esta cámara."
      />

      {/* ── Captura directa / vía NVR (2026-06-21) ──────────────────────── */}
      <div style={{
        marginTop: 16, padding: 12, borderRadius: 6,
        border: '1px solid var(--border)', background: 'var(--bg-elev-1)',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10,
        }}>
          Captura del stream
        </div>
        <Field label="Modo" hint="'Vía NVR' (default): el daemon abre RTSP contra el NVR usando channel. 'Directa': el daemon abre RTSP contra la IP propia de la cámara. Las grabaciones SIEMPRE quedan en el NVR (playback usa nvr_id + channel)." >
          <select
            value={draft.capture_mode || 'via_nvr'}
            onChange={e => setDraft({ ...draft, capture_mode: e.target.value })}
            style={{
              width: '100%', padding: '8px 10px', fontSize: 13,
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', borderRadius: 4,
            }}
          >
            <option value="via_nvr">Vía NVR (default)</option>
            <option value="direct">Directa (RTSP contra IP de la cámara)</option>
          </select>
        </Field>
        {draft.capture_mode === 'direct' && (
          <>
            <Field label="IP de la cámara" hint="Dirección IP de la cámara en la LAN del cliente. Ej: 192.168.1.51">
              <input
                type="text"
                value={draft.ip_direct || ''}
                placeholder="192.168.1.51"
                onChange={e => setDraft({ ...draft, ip_direct: e.target.value || null })}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: 13,
                  background: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 4,
                }}
              />
            </Field>
            <Field label="Usuario" hint="Usuario propio de la cámara (no del NVR).">
              <input
                type="text"
                value={draft.username_direct || ''}
                placeholder="admin"
                onChange={e => setDraft({ ...draft, username_direct: e.target.value || null })}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: 13,
                  background: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 4,
                }}
              />
            </Field>
            <Field label="Contraseña" hint="Contraseña de la cámara. Se guarda en plain text (deuda: sops/age para Copec).">
              <input
                type="password"
                value={draft.password_direct || ''}
                placeholder="••••••••"
                onChange={e => setDraft({ ...draft, password_direct: e.target.value || null })}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: 13,
                  background: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 4,
                }}
              />
            </Field>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, padding: 8, background: 'rgba(0,145,255,0.06)', borderRadius: 4 }}>
              El daemon edge va a armar la URL RTSP directo a la cámara según marca.
              Recordá: el NVR ({draft.nvr_id ? `id ${draft.nvr_id}` : 'no asignado'}) sigue siendo el destino del playback histórico.
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function TabPerson({ draft, has, toggle, setDraft }) {
  return (
    <div>
      <CheckRow
        label="Detección de personas"
        checked={has('person')}
        onChange={() => toggle('person')}
        hint="Identifica personas con YOLO. Base de loitering, cruce de línea, alertas."
      />
      <CheckRow
        label={tCem("Alertar TODA detección de persona","Alert on EVERY person detection")}
        checked={draft.person_alert}
        onChange={v => setDraft({ ...draft, person_alert: v })}
        hint="Emite incidente high severity por CADA persona detectada. Solo para áreas sensibles (patios cerrados, perímetro de noche)."
      />
      <CheckRow
        label="Reconocimiento facial"
        checked={draft.face_recognition}
        onChange={v => setDraft({ ...draft, face_recognition: v })}
        hint="Match contra base enrolada. Requiere watchlist configurada."
      />
      <CheckRow
        label="Detección de arma"
        checked={draft.weapon_detection}
        onChange={v => setDraft({ ...draft, weapon_detection: v })}
        hint="Modelo especializado, agrega carga CPU. Activá solo en cámaras de cara visible."
      />
    </div>
  )
}

function TabVehicle({ draft, has, toggle, setDraft }) {
  return (
    <div>
      <CheckRow
        label="Detección de vehículos"
        checked={has('vehicle')}
        onChange={() => toggle('vehicle')}
        hint="Auto, camioneta, camión, bus, moto, bicicleta. Color se detecta automáticamente."
      />
      <CheckRow
        label="LPR — Lectura de patentes"
        checked={has('lpr')}
        onChange={() => toggle('lpr')}
        hint="Solo si la cámara tiene buen ángulo y resolución de patente legible (>40px de alto)."
      />
      <CheckRow
        label="Reconocimiento de marca (CLIP)"
        checked={draft.make_recognition}
        onChange={v => setDraft({ ...draft, make_recognition: v })}
        hint="Detecta marca (Toyota, Ford, etc.) con CLIP. ~250MB extra de RAM. Recomendado solo en LPR principal."
      />
    </div>
  )
}

function TabLoitering({ draft, setDraft }) {
  const active = (draft.loitering_seconds ?? 0) > 0
  return (
    <div>
      <CheckRow
        label="Detección de merodeo"
        checked={active}
        onChange={v => setDraft({
          ...draft,
          loitering_seconds: v ? 30 : 0,
          loitering_max_movement_px: v ? 80 : null,
        })}
        hint="Alerta si una persona permanece N segundos en un lugar con movimiento menor a M píxeles."
      />
      {active && (
        <>
          <Field label="Segundos quieta antes de alertar" hint="Más bajo = más sensible, más falsos positivos. Para calle pública 0 (apagado). Patio cerrado 30. Estacionamiento 20.">
            <NumberInput
              value={draft.loitering_seconds}
              min={1} max={300} step={5}
              onChange={v => setDraft({ ...draft, loitering_seconds: v ?? 30 })}
            />
          </Field>
          <Field label="Tolerancia de movimiento (px)" hint="Cuánto puede moverse en el frame sin perder el contador. Más bajo = más estricto. Default 80.">
            <NumberInput
              value={draft.loitering_max_movement_px}
              min={10} max={300} step={10}
              onChange={v => setDraft({ ...draft, loitering_max_movement_px: v ?? 80 })}
            />
          </Field>
        </>
      )}
    </div>
  )
}

// ─── Editor visual de línea sobre snapshot real (Sprint B1) ────────────────
//
// En vez de inputs numéricos, el operador:
//   - Ve el snapshot real de la cámara como background
//   - Arrastra los 2 puntos (A y B) con el mouse para ajustar la línea
//   - Ve una flecha al medio indicando dirección de cruce
//
// Frame original (donde el daemon edge espera coords): asumimos 1920×1080.
// Canvas display: 480×270 (16:9, mismo ratio). Conversión proporcional.
function LineEditorVisual({ camera, draft, setDraft, token }) {
  const FRAME_W = 1920
  const FRAME_H = 1080
  // Display dimensions ajustadas al ancho del drawer (420px - 32 padding = ~388).
  // Si el stage es más ancho que el drawer, el punto B queda fuera de pantalla
  // y no se puede arrastrar. 16:9 → 380×214.
  const DISP_W = 380
  const DISP_H = 214

  const [snapshotUrl, setSnapshotUrl] = useState(null)
  const [snapErr, setSnapErr]         = useState(null)
  const [snapLoading, setSnapLoading] = useState(true)
  const [img] = useImage(snapshotUrl)

  // Cargar snapshot con auth (fetch con Bearer, transformar a blob URL)
  const loadSnapshot = useCallback(async () => {
    if (!camera?.nvr_id || !camera?.channel || !token) {
      setSnapErr('Cámara sin nvr_id o channel — snapshot no disponible')
      setSnapLoading(false)
      return
    }
    setSnapLoading(true); setSnapErr(null)
    try {
      const r = await fetch(
        `/api/nvr/${camera.nvr_id}/snapshot/${camera.channel}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      setSnapshotUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(blob)
      })
    } catch (e) {
      setSnapErr(e.message || String(e))
    } finally {
      setSnapLoading(false)
    }
  }, [camera?.nvr_id, camera?.channel, token])

  useEffect(() => { loadSnapshot() }, [loadSnapshot])

  const line   = draft.protected_line
  const active = Array.isArray(line) && line.length === 2

  // Convertir coords frame original ↔ display
  const f2d = (pt) => [pt[0] * DISP_W / FRAME_W, pt[1] * DISP_H / FRAME_H]
  const d2f = (x, y) => [
    Math.round(Math.max(0, Math.min(FRAME_W, x * FRAME_W / DISP_W))),
    Math.round(Math.max(0, Math.min(FRAME_H, y * FRAME_H / DISP_H))),
  ]

  if (!active) {
    return (
      <div>
        <CheckRow
          label="Cruce de línea protegida"
          checked={false}
          onChange={v => setDraft({
            ...draft,
            protected_line: v ? [[100, 540], [1820, 540]] : null,
          })}
          hint="Alerta cuando una persona o vehículo cruza la línea. Se dibuja con el mouse sobre el snapshot real de la cámara."
        />
      </div>
    )
  }

  const [ax, ay] = f2d(line[0])
  const [bx, by] = f2d(line[1])
  // Punto medio para mostrar la flecha de dirección
  const mx = (ax + bx) / 2
  const my = (ay + by) / 2
  // Vector perpendicular para la flecha (rotado 90°)
  const dx = bx - ax
  const dy = by - ay
  const len = Math.sqrt(dx * dx + dy * dy) || 1
  const arrowLen = 30
  // perpendicular (rotate -90°)
  const px = -dy / len * arrowLen
  const py =  dx / len * arrowLen

  return (
    <div>
      <CheckRow
        label="Cruce de línea protegida"
        checked={true}
        onChange={v => setDraft({
          ...draft,
          protected_line: v ? line : null,
        })}
        hint="La línea se dibuja sobre el snapshot real. Arrastrá los puntos A y B."
      />

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
          Snapshot de la cámara (refresca cada vez que abrís el tab)
        </div>
        {snapLoading && (
          <div style={{
            width: DISP_W, height: DISP_H,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg-elev-1)', border: '1px solid var(--border)',
            borderRadius: 4, color: 'var(--text-muted)', fontSize: 12,
          }}>
            Cargando snapshot...
          </div>
        )}
        {snapErr && !snapLoading && (
          <div style={{
            width: DISP_W, padding: 12,
            background: 'rgba(217,48,37,0.1)', border: '1px solid #d93025',
            color: '#d93025', borderRadius: 4, fontSize: 11,
          }}>
            No se pudo cargar el snapshot: {snapErr}
            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
              Editá las coordenadas numéricas abajo como fallback.
            </div>
          </div>
        )}
        {!snapLoading && !snapErr && (
          <Stage width={DISP_W} height={DISP_H} style={{
            background: 'var(--bg-elev-1)', border: '1px solid var(--border)',
            borderRadius: 4,
          }}>
            <Layer>
              {img && <KImage image={img} width={DISP_W} height={DISP_H} />}
              {/* Línea principal */}
              <Arrow
                points={[ax, ay, bx, by]}
                stroke="#d93025" fill="#d93025"
                strokeWidth={3}
                pointerLength={0} pointerWidth={0}
                shadowColor="black" shadowBlur={6} shadowOpacity={0.6}
              />
              {/* Flecha de dirección perpendicular al centro */}
              <Arrow
                points={[mx, my, mx + px, my + py]}
                stroke="#21a366" fill="#21a366"
                strokeWidth={2}
                pointerLength={8} pointerWidth={8}
              />
              {/* Punto A (rojo) — etiquetado para accesibilidad daltonismo */}
              <Group
                x={ax} y={ay} draggable
                dragBoundFunc={(pos) => ({
                  x: Math.max(0, Math.min(DISP_W, pos.x)),
                  y: Math.max(0, Math.min(DISP_H, pos.y)),
                })}
                onDragMove={(e) => {
                  const [fx, fy] = d2f(e.target.x(), e.target.y())
                  setDraft({ ...draft, protected_line: [[fx, fy], line[1]] })
                }}
              >
                <Circle radius={10} fill="#d93025" stroke="white" strokeWidth={2} />
                <Text text="A" x={-4} y={-6} fontSize={12} fontStyle="bold" fill="white" />
              </Group>
              {/* Punto B */}
              <Group
                x={bx} y={by} draggable
                dragBoundFunc={(pos) => ({
                  x: Math.max(0, Math.min(DISP_W, pos.x)),
                  y: Math.max(0, Math.min(DISP_H, pos.y)),
                })}
                onDragMove={(e) => {
                  const [fx, fy] = d2f(e.target.x(), e.target.y())
                  setDraft({ ...draft, protected_line: [line[0], [fx, fy]] })
                }}
              >
                <Circle radius={10} fill="#d93025" stroke="white" strokeWidth={2} />
                <Text text="B" x={-4} y={-6} fontSize={12} fontStyle="bold" fill="white" />
              </Group>
            </Layer>
          </Stage>
        )}
      </div>

      <button
        onClick={loadSnapshot}
        style={{
          padding: '6px 12px', fontSize: 11,
          border: '1px solid var(--border)', borderRadius: 4,
          background: 'transparent', color: 'var(--text-primary)',
          cursor: 'pointer', marginBottom: 12,
        }}
      >
        Refrescar snapshot
      </button>

      <div style={{
        padding: 8, background: 'rgba(255,255,255,0.03)',
        borderRadius: 4, fontSize: 11, color: 'var(--text-muted)',
        marginBottom: 12,
      }}>
        <div>Punto A: ({line[0][0]}, {line[0][1]}) — rojo, izquierda</div>
        <div>Punto B: ({line[1][0]}, {line[1][1]}) — rojo, derecha</div>
        <div>Flecha verde: dirección perpendicular del cruce que dispara alerta</div>
      </div>

      {/* Inputs numéricos como fallback editable */}
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)' }}>
          Editar coordenadas manualmente (avanzado)
        </summary>
        <div style={{ marginTop: 8 }}>
          <Field label="Punto A — x, y (px del frame 1920×1080)">
            <div style={{ display: 'flex', gap: 8 }}>
              <NumberInput
                value={line[0][0]} min={0} max={FRAME_W}
                onChange={v => setDraft({ ...draft, protected_line: [[v ?? 0, line[0][1]], line[1]] })}
              />
              <NumberInput
                value={line[0][1]} min={0} max={FRAME_H}
                onChange={v => setDraft({ ...draft, protected_line: [[line[0][0], v ?? 0], line[1]] })}
              />
            </div>
          </Field>
          <Field label="Punto B — x, y (px del frame)">
            <div style={{ display: 'flex', gap: 8 }}>
              <NumberInput
                value={line[1][0]} min={0} max={FRAME_W}
                onChange={v => setDraft({ ...draft, protected_line: [line[0], [v ?? 0, line[1][1]]] })}
              />
              <NumberInput
                value={line[1][1]} min={0} max={FRAME_H}
                onChange={v => setDraft({ ...draft, protected_line: [line[0], [line[1][0], v ?? 0]] })}
              />
            </div>
          </Field>
        </div>
      </details>
    </div>
  )
}

function TabLine({ draft, setDraft, camera, token }) {
  return <LineEditorVisual camera={camera} draft={draft} setDraft={setDraft} token={token} />
}


// ─── Tab Calibración silenciosa (shadow mode) ──────────────────────────────
//
// Sprint B2. El operador activa shadow_mode en la cámara. Durante N horas el
// daemon edge sigue evaluando reglas pero NO emite alertas al feed principal;
// las envía a /api/cameras/{id}/shadow_alerts. Al cabo de 24h, este tab
// muestra el resumen: qué reglas hubieran disparado cuántas alertas. El
// operador decide aprobar (limpia + apaga shadow) o ajustar threshold.
function TabShadow({ draft, setDraft, camera, token }) {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr]         = useState(null)
  const [sinceHours, setSinceHours] = useState(24)

  const shadowOn = draft.config_extras?.shadow_mode === true

  const loadSummary = useCallback(async () => {
    if (!camera?.id || !token) return
    setLoading(true); setErr(null)
    try {
      const r = await fetch(
        `/api/cameras/${camera.id}/shadow_summary?since_hours=${sinceHours}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      setSummary(data)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [camera?.id, token, sinceHours])

  useEffect(() => { loadSummary() }, [loadSummary])

  const toggleShadow = (v) => {
    const cfg = { ...(draft.config_extras || {}) }
    if (v) {
      cfg.shadow_mode = true
      cfg.shadow_mode_started_at = new Date().toISOString()
    } else {
      cfg.shadow_mode = false
    }
    setDraft({ ...draft, config_extras: cfg })
  }

  const startedAt = draft.config_extras?.shadow_mode_started_at
  const hoursRunning = startedAt
    ? Math.floor((Date.now() - new Date(startedAt).getTime()) / 3_600_000)
    : null

  const clearShadowAlerts = async (ruleKind) => {
    if (!confirm(`Borrar shadow alerts de '${ruleKind || 'todas las reglas'}' para esta cámara?`)) return
    const qs = ruleKind ? `?rule_kind=${ruleKind}` : ''
    const r = await fetch(`/api/cameras/${camera.id}/shadow_alerts${qs}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (r.ok) loadSummary()
  }

  return (
    <div>
      <CheckRow
        label="Modo calibración silenciosa"
        checked={shadowOn}
        onChange={toggleShadow}
        hint={
          shadowOn
            ? "Las reglas se evalúan pero las alertas NO entran al feed principal. Se registran acá para revisión sin contaminar el feed operacional."
            : "Activá para probar reglas nuevas 24h antes de que generen incidentes reales. Útil para calibrar thresholds sin generar ruido."
        }
      />

      {shadowOn && startedAt && (
        <div style={{
          fontSize: 11, color: 'var(--text-muted)',
          padding: 8, background: 'rgba(255,255,255,0.03)',
          borderRadius: 4, marginBottom: 12,
        }}>
          Modo activo desde: {new Date(startedAt).toLocaleString()}
          {' '}({hoursRunning ?? '?'} horas corriendo)
        </div>
      )}

      {/* Selector de ventana */}
      <Field label="Ventana de revisión">
        <select
          value={sinceHours}
          onChange={e => setSinceHours(Number(e.target.value))}
          style={{
            width: '100%', padding: '6px 10px', fontSize: 12,
            background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
          }}
        >
          <option value={1}>Última 1 hora</option>
          <option value={6}>Últimas 6 horas</option>
          <option value={24}>Últimas 24 horas</option>
          <option value={72}>Últimos 3 días</option>
          <option value={168}>Última semana</option>
        </select>
      </Field>

      {/* Resumen */}
      <div style={{ marginTop: 16 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 8,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            Resumen de alertas hipotéticas
          </div>
          <button
            onClick={loadSummary}
            disabled={loading}
            style={{
              padding: '4px 10px', fontSize: 11,
              border: '1px solid var(--border)', borderRadius: 4,
              background: 'transparent', color: 'var(--text-primary)',
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Cargando...' : 'Refrescar'}
          </button>
        </div>

        {err && (
          <div style={{
            padding: 8, fontSize: 11,
            background: 'rgba(217,48,37,0.1)', border: '1px solid #d93025',
            color: '#d93025', borderRadius: 4, marginBottom: 8,
          }}>
            {err}
          </div>
        )}

        {summary && summary.total_alerts === 0 && (
          <div style={{
            padding: 16, textAlign: 'center', fontSize: 12,
            background: 'var(--bg-elev-1)', border: '1px dashed var(--border)',
            borderRadius: 4, color: 'var(--text-muted)',
          }}>
            Sin alertas hipotéticas en este rango.
            <div style={{ marginTop: 6, fontSize: 11 }}>
              {shadowOn
                ? 'El daemon está observando pero todavía no detectó nada.'
                : 'Activá el modo calibración arriba para empezar a registrar.'}
            </div>
          </div>
        )}

        {summary && summary.total_alerts > 0 && (
          <div>
            <div style={{
              padding: 12, marginBottom: 10,
              background: 'rgba(232,167,4,0.08)', border: '1px solid #e8a704',
              borderRadius: 4,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {summary.total_alerts} alertas hubieran sido emitidas en {summary.since_hours}h
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Equivale a ~{(summary.total_alerts / summary.since_hours).toFixed(1)} alertas por hora.
                Si te parece mucho, ajustá los thresholds antes de activar la regla en producción.
              </div>
            </div>

            {summary.by_rule_kind.map(r => (
              <div key={r.rule_kind} style={{
                padding: 10, marginBottom: 6,
                background: 'var(--bg-elev-1)', border: '1px solid var(--border)',
                borderRadius: 4,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {r.rule_kind} · {r.total} alertas
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      Por severidad: {Object.entries(r.by_severity).map(([s, n]) => `${s}=${n}`).join(', ')}
                    </div>
                    {r.first_ts && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                        Primera: {new Date(r.first_ts).toLocaleString()} · Última: {new Date(r.last_ts).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => clearShadowAlerts(r.rule_kind)}
                    style={{
                      padding: '4px 10px', fontSize: 11,
                      border: '1px solid var(--border)', borderRadius: 4,
                      background: 'transparent', color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-muted)' }}>
        <strong>Flujo recomendado:</strong>
        <ol style={{ marginTop: 4, paddingLeft: 18 }}>
          <li>Activá calibración silenciosa</li>
          <li>Configurá los thresholds de las reglas (tabs Persona/Vehículo/Merodeo/Línea)</li>
          <li>Guardá → el daemon empieza a evaluar en shadow</li>
          <li>Volvé acá en 24h para ver cuántas alertas hubiera generado</li>
          <li>Si te parece bien → desactivá calibración y guardá. Las reglas pasan a producción.</li>
        </ol>
      </div>
    </div>
  )
}


// ─── Tab Horario (Sprint B3) ──────────────────────────────────────────────
// Activa o silencia reglas según día de la semana + hora local del sitio.
// Las detecciones forenses (NVR + crops locales) NO se afectan — esto
// solo controla qué eventos viajan al cloud como alerta.

const DAY_LABELS_FULL = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo']
const DAY_LABELS_SHORT = ['Lu','Ma','Mi','Ju','Vi','Sá','Do']

const SCHEDULE_PRESETS = {
  diurno: {
    label: 'Diurno (06:00 a 22:00, todos los días)',
    hours: Array.from({length:24}, (_,h) => h >= 6 && h < 22),
    daily: true,
  },
  nocturno: {
    label: 'Nocturno (22:00 a 06:00, todos los días)',
    hours: Array.from({length:24}, (_,h) => h >= 22 || h < 6),
    daily: true,
  },
  comercial_lv: {
    label: 'Comercial (Lun-Vie, 09:00 a 21:00)',
    hours: Array.from({length:24}, (_,h) => h >= 9 && h < 21),
    daily: false,   // sat+sun off
  },
}

function emptyGrid() {
  return Array.from({length: 7}, () => Array(24).fill(true))
}

function presetToGrid(preset) {
  const cfg = SCHEDULE_PRESETS[preset]
  if (!cfg) return emptyGrid()
  return Array.from({length: 7}, (_, d) => {
    if (!cfg.daily && (d === 5 || d === 6)) return Array(24).fill(false)
    return [...cfg.hours]
  })
}

function describeGrid(grid) {
  // Devuelve texto humano "Lun-Vie 09:00-21:00 · Sáb-Dom silenciado"
  // Accesibilidad: Paulo es daltónico, esto es la fuente de verdad textual.
  if (!grid) return 'Activa 24/7'
  let total = 0
  for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) if (grid[d][h]) total++
  if (total === 168) return 'Activa 24/7'
  if (total === 0) return 'Silenciada todo el tiempo (ninguna hora activa)'
  // Agrupar por día
  const lines = []
  for (let d = 0; d < 7; d++) {
    const active = grid[d]
    const onCount = active.filter(Boolean).length
    if (onCount === 0) {
      lines.push(`${DAY_LABELS_FULL[d]}: silenciado`)
    } else if (onCount === 24) {
      lines.push(`${DAY_LABELS_FULL[d]}: activo 24h`)
    } else {
      // Rangos continuos
      const ranges = []
      let start = null
      for (let h = 0; h <= 24; h++) {
        if (h < 24 && active[h]) {
          if (start === null) start = h
        } else {
          if (start !== null) {
            ranges.push(`${String(start).padStart(2,'0')}:00-${String(h).padStart(2,'0')}:00`)
            start = null
          }
        }
      }
      lines.push(`${DAY_LABELS_FULL[d]}: ${ranges.join(', ')}`)
    }
  }
  return lines.join(' · ')
}

function TabSchedule({ draft, setDraft }) {
  const extras = draft.config_extras || {}
  const sch = extras.schedule || { mode: 'always', tz_offset: -3 }
  const mode = sch.mode || 'always'
  const grid = sch.mode === 'custom' && Array.isArray(sch.grid) && sch.grid.length === 7
    ? sch.grid
    : (sch.mode === 'preset' ? presetToGrid(sch.preset) : emptyGrid())

  const writeSchedule = (newSch) => {
    setDraft({
      ...draft,
      config_extras: { ...extras, schedule: newSch },
    })
  }

  const setMode = (newMode) => {
    if (newMode === 'always') {
      writeSchedule({ mode: 'always', tz_offset: sch.tz_offset || -3 })
    } else if (newMode === 'preset') {
      writeSchedule({ mode: 'preset', preset: sch.preset || 'comercial_lv', tz_offset: sch.tz_offset || -3 })
    } else {
      writeSchedule({ mode: 'custom', grid: grid.map(d => [...d]), tz_offset: sch.tz_offset || -3 })
    }
  }

  const setPreset = (preset) => {
    writeSchedule({ mode: 'preset', preset, tz_offset: sch.tz_offset || -3 })
  }

  const toggleCell = (day, hour) => {
    if (mode !== 'custom') return
    const next = grid.map(d => [...d])
    next[day][hour] = !next[day][hour]
    writeSchedule({ mode: 'custom', grid: next, tz_offset: sch.tz_offset || -3 })
  }

  const setDayState = (day, state) => {
    if (mode !== 'custom') return
    const next = grid.map(d => [...d])
    next[day] = Array(24).fill(state)
    writeSchedule({ mode: 'custom', grid: next, tz_offset: sch.tz_offset || -3 })
  }

  const description = describeGrid(mode === 'always' ? null : grid)

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ fontSize:13, color:'var(--text-muted)' }}>
        Define cuándo las reglas de esta cámara generan alertas reales.
        Las detecciones forenses (crops, video NVR) NO se afectan.
      </div>

      {/* Modo */}
      <div>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Modo</div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {[
            ['always',  'Siempre activa (24/7)'],
            ['preset',  'Preset'],
            ['custom',  'Personalizado 7×24'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              aria-pressed={mode === id}
              style={{
                padding:'6px 10px',
                fontSize:12,
                border:'1px solid var(--border)',
                background: mode === id ? '#0091ff' : 'transparent',
                color: mode === id ? '#fff' : 'var(--text-primary)',
                borderRadius:4, cursor:'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Preset selector */}
      {mode === 'preset' && (
        <div>
          <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Preset</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {Object.entries(SCHEDULE_PRESETS).map(([id, p]) => (
              <label
                key={id}
                style={{
                  display:'flex', alignItems:'center', gap:8,
                  padding:8, border:'1px solid var(--border)', borderRadius:4,
                  cursor:'pointer',
                  background: sch.preset === id ? 'rgba(0,145,255,0.12)' : 'transparent',
                }}
              >
                <input
                  type="radio"
                  name="preset"
                  checked={sch.preset === id}
                  onChange={() => setPreset(id)}
                />
                <span style={{ fontSize:13 }}>{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Grilla 7×24 (custom) */}
      {mode === 'custom' && (
        <div>
          <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>
            Grilla — clic en una celda para alternar
          </div>
          <div style={{ overflowX:'auto', fontSize:10 }}>
            <table style={{ borderCollapse:'collapse', width:'100%' }}>
              <thead>
                <tr>
                  <th style={{ padding:2, textAlign:'left' }}></th>
                  {Array.from({length:24}, (_, h) => (
                    <th key={h} style={{ padding:2, fontWeight:400, color:'var(--text-muted)' }}>
                      {h}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {grid.map((day, d) => (
                  <tr key={d}>
                    <td style={{ padding:2, fontWeight:600 }}>{DAY_LABELS_SHORT[d]}</td>
                    {day.map((on, h) => (
                      <td
                        key={h}
                        onClick={() => toggleCell(d, h)}
                        title={on
                          ? `${DAY_LABELS_FULL[d]} ${String(h).padStart(2,'0')}:00 ACTIVA`
                          : `${DAY_LABELS_FULL[d]} ${String(h).padStart(2,'0')}:00 silenciada`}
                        style={{
                          width:14, height:18,
                          // patrón texto + color para daltónicos: ✓ activa, · silenciada
                          background: on ? '#0091ff' : 'var(--bg-surface)',
                          border:'1px solid var(--border)',
                          textAlign:'center',
                          color: on ? '#fff' : 'var(--text-muted)',
                          cursor:'pointer',
                          fontSize:9,
                        }}
                      >
                        {on ? '✓' : '·'}
                      </td>
                    ))}
                    <td style={{ padding:'0 4px' }}>
                      <button
                        onClick={() => setDayState(d, true)}
                        title={`Activar todo el día ${DAY_LABELS_FULL[d]}`}
                        style={{ fontSize:10, padding:'1px 4px', marginRight:2 }}
                      >
                        24h
                      </button>
                      <button
                        onClick={() => setDayState(d, false)}
                        title={`Silenciar todo el día ${DAY_LABELS_FULL[d]}`}
                        style={{ fontSize:10, padding:'1px 4px' }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resumen textual — fuente de verdad accesible */}
      <div style={{
        padding:10, border:'1px solid var(--border)', borderRadius:4,
        background:'rgba(0,145,255,0.08)', fontSize:12,
      }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>Resumen:</div>
        <div style={{ whiteSpace:'pre-line' }}>{description}</div>
      </div>

      <div style={{ fontSize:11, color:'var(--text-muted)' }}>
        Hora local: GMT{sch.tz_offset >= 0 ? '+' : ''}{sch.tz_offset || -3} (Chile continental).
        Fuera del horario, las reglas se evalúan pero el evento se descarta
        antes de salir del M4. Los crops forenses se guardan igual.
      </div>
    </div>
  )
}


// ─── Tab Alertas (ex PoliciesView, unificado en el drawer) ────────────────
// Decide qué eventos de ESTA cámara generan incidente y push al guardia
// vs cuáles solo quedan archivados para búsqueda forense.
//
// Backend: GET /api/policies/incident?site_id=X devuelve TODAS las políticas
//          del sitio. Filtramos por camera_name acá.
//          POST /api/policies/incident con {site_id, camera_name, mode,
//          allowed_types, min_duration_s} crea o reemplaza.
//
// Modos:
//   • all     — cualquier detección genera incidente
//   • custom  — solo los tipos elegidos (con duración mínima opcional)
//   • none    — solo forense, no genera alerta
//
// Accesibilidad: cada modo lleva texto + ícono, no solo color (Paulo
// es daltónico).

const ALERT_EVENT_LABEL = {
  weapon_detected   : { label: 'Arma',            icon: '🔫' },
  loitering_detected: { label: 'Merodeo',         icon: '⏱' },
  line_crossing     : { label: 'Cruce perímetro', icon: '🚧' },
  face_match        : { label: 'Watchlist cara',  icon: '👤' },
  person_detected   : { label: 'Persona',         icon: '👀' },
  vehicle_detected  : { label: 'Vehículo',        icon: '🚗' },
}

const ALERT_MODE_META = {
  all: {
    label: tCem('Alerta total','Full alert'),
    color: '#ef4444',
    desc: 'Cualquier detección genera incidente y push al guardia.',
  },
  custom: {
    label: 'Selectiva',
    color: '#f59e0b',
    desc: 'Solo los tipos seleccionados generan incidente.',
  },
  none: {
    label: 'Solo forense',
    color: '#6b7280',
    desc: 'No genera alertas — los eventos siguen guardándose para investigación.',
  },
}

const ALERT_PRESETS = [
  { label: 'Cámara crítica',     mode: 'all'    },
  { label: 'Solo merodeo ≥ 5s',  mode: 'custom', allowed_types: ['loitering_detected'], min_duration_s: 5 },
  { label: 'Solo perímetro',     mode: 'custom', allowed_types: ['line_crossing'] },
  { label: 'Silenciar',          mode: 'none'   },
]

function TabAlerts({ camera, siteId, token }) {
  const [policy, setPolicy] = useState(null)   // política actual de esta cámara
  const [draft, setDraft]   = useState({ mode: 'all', allowed_types: null, min_duration_s: null })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  // Carga inicial de la política
  useEffect(() => {
    if (!siteId || !camera?.name || !token) return
    setLoading(true)
    setError(null)
    fetch(`/api/policies/incident?site_id=${siteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const list = d.policies || []
        const mine = list.find(p => p.camera_name === camera.name)
        if (mine) {
          setPolicy(mine)
          setDraft({
            mode: mine.mode || 'all',
            allowed_types: mine.allowed_types || null,
            min_duration_s: mine.min_duration_s ?? null,
          })
        } else {
          // sin política propia → fallback (asumimos 'all' por default)
          setPolicy(null)
          setDraft({ mode: 'all', allowed_types: null, min_duration_s: null })
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [siteId, camera?.name, token])

  const dirty = (() => {
    const cur = policy
      ? { mode: policy.mode, allowed_types: policy.allowed_types || null, min_duration_s: policy.min_duration_s ?? null }
      : { mode: 'all', allowed_types: null, min_duration_s: null }
    return JSON.stringify(draft) !== JSON.stringify(cur)
  })()

  const save = async () => {
    setSaving(true); setError(null)
    try {
      const body = {
        site_id        : siteId,
        camera_name    : camera.name,
        mode           : draft.mode,
        allowed_types  : draft.allowed_types || null,
        min_duration_s : draft.min_duration_s ?? null,
      }
      const r = await fetch('/api/policies/incident', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(await r.text())
      const d = await r.json()
      setPolicy(d.policy)
      setSavedAt(new Date())
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const toggleType = (t) => {
    const cur = draft.allowed_types || []
    const next = cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]
    setDraft({ ...draft, allowed_types: next.length ? next : null })
  }

  if (loading) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cargando política…</div>
  }

  const meta = ALERT_MODE_META[draft.mode] || ALERT_MODE_META.all
  const hasLoitering = (draft.allowed_types || []).includes('loitering_detected')

  return (
    <div>
      <Field label="Política actual" hint={policy ? null : 'Sin regla propia. Aplica el fallback del sitio (asumido: Alerta total).'}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '6px 0' }}>
          {policy
            ? <>Modo: <b>{ALERT_MODE_META[policy.mode]?.label || policy.mode}</b></>
            : <>Sin regla propia configurada para esta cámara.</>}
        </div>
      </Field>

      <Field label="Presets rápidos">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ALERT_PRESETS.map(p => (
            <button key={p.label}
              onClick={() => setDraft({
                mode: p.mode,
                allowed_types: p.allowed_types || null,
                min_duration_s: p.min_duration_s ?? null,
              })}
              style={{
                padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                border: '1px solid var(--border)', background: 'var(--bg-elev-1)',
                color: 'var(--text-secondary)', cursor: 'pointer',
              }}>{p.label}</button>
          ))}
        </div>
      </Field>

      <Field label="Modo de alerta">
        <div style={{ display: 'flex', gap: 6 }}>
          {Object.entries(ALERT_MODE_META).map(([key, m]) => {
            const active = draft.mode === key
            return (
              <button key={key} onClick={() => setDraft({ ...draft, mode: key })}
                style={{
                  flex: 1, padding: '10px 6px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  border: `1px solid ${active ? m.color : 'var(--border)'}`,
                  background: active ? `${m.color}22` : 'var(--bg-elev-1)',
                  color: active ? m.color : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}>
                {m.label}
              </button>
            )
          })}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--text-muted)', marginTop: 6,
          padding: '6px 8px', background: `${meta.color}11`,
          borderLeft: `3px solid ${meta.color}`, borderRadius: 3,
        }}>
          {meta.desc}
        </div>
      </Field>

      {draft.mode === 'custom' && (
        <>
          <Field label="Tipos que generan incidente"
            hint="Si no eliges ninguno la cámara queda en silencio efectivo.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
              {Object.entries(ALERT_EVENT_LABEL).map(([key, e]) => {
                const checked = (draft.allowed_types || []).includes(key)
                return (
                  <button key={key} onClick={() => toggleType(key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                      border: `1px solid ${checked ? '#0091ff' : 'var(--border)'}`,
                      background: checked ? 'rgba(0,145,255,0.10)' : 'var(--bg-elev-1)',
                      color: checked ? '#0091ff' : 'var(--text-secondary)',
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                    <span style={{ fontSize: 14 }}>{e.icon}</span>
                    {e.label} {checked ? '[ON]' : ''}
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="Duración mínima merodeo"
            hint={hasLoitering ? 'Solo cuenta como incidente si el merodeo supera este tiempo.' : 'Solo aplica si "Merodeo" está marcado arriba.'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="range" min={0} max={60} step={1}
                value={draft.min_duration_s ?? 0}
                disabled={!hasLoitering}
                onChange={e => setDraft({ ...draft, min_duration_s: Number(e.target.value) || null })}
                style={{ flex: 1 }} />
              <span style={{ fontSize: 12, fontWeight: 700, minWidth: 60, textAlign: 'right' }}>
                {draft.min_duration_s ? `${draft.min_duration_s}s` : 'sin filtro'}
              </span>
            </div>
          </Field>
        </>
      )}

      {error && (
        <div style={{
          fontSize: 11, color: '#d93025', padding: '6px 8px',
          background: 'rgba(217,48,37,0.1)', borderRadius: 4, marginTop: 10,
        }}>
          Error: {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button onClick={save} disabled={!dirty || saving}
          style={{
            padding: '8px 14px', fontSize: 12, fontWeight: 700,
            border: 'none', borderRadius: 4,
            background: dirty ? '#21a366' : 'var(--border)',
            color: 'white',
            cursor: dirty && !saving ? 'pointer' : 'default',
            opacity: dirty && !saving ? 1 : 0.6,
          }}>
          {saving ? tCem('Guardando…','Saving…') : tCem('Guardar política','Save policy')}
        </button>
        {savedAt && !dirty && !saving && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Guardado {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  )
}


// ─── Tab Historial (Sprint B4) ────────────────────────────────────────────
// Lista de versiones del config de la cámara. Cada save crea una versión
// con snapshot del estado anterior. Operador puede restaurar a cualquier
// versión y eso crea otra versión con el estado actual.

function TabHistory({ camera, token }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [restoreError, setRestoreError] = useState(null)
  const [restoring, setRestoring] = useState(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiFetch(token, `/api/cameras/${camera.id}/versions`)
      const data = await r.json()
      setVersions(Array.isArray(data) ? data : [])
    } catch (e) {
      console.warn('versions_load_failed', e)
      setVersions([])
    } finally {
      setLoading(false)
    }
  }, [camera.id, token])

  useEffect(() => { reload() }, [reload])

  const onRestore = async (version_num) => {
    const ok = window.confirm(
      `¿Restaurar a versión ${version_num}? La config actual se guarda como una nueva versión.`
    )
    if (!ok) return
    setRestoring(version_num)
    setRestoreError(null)
    try {
      const r = await apiFetch(token, `/api/cameras/${camera.id}/versions/${version_num}/restore`, {
        method: 'POST',
      })
      if (!r.ok) {
        const t = await r.text()
        throw new Error(t || `HTTP ${r.status}`)
      }
      await reload()
    } catch (e) {
      setRestoreError(String(e.message || e))
    } finally {
      setRestoring(null)
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:13, color:'var(--text-muted)' }}>
        Cada guardado crea automáticamente una versión con el estado anterior.
        Podés restaurar cualquier versión — eso vuelve a crear una más
        con la config actual, no se pierde nada.
      </div>

      {loading && (
        <div style={{ fontSize:12, color:'var(--text-muted)' }}>Cargando historial...</div>
      )}

      {!loading && versions.length === 0 && (
        <div style={{
          padding:14, border:'1px solid var(--border)', borderRadius:4,
          fontSize:12, color:'var(--text-muted)', textAlign:'center',
        }}>
          Esta cámara aún no tiene versiones guardadas. La primera versión
          se creará la próxima vez que hagas un cambio aquí.
        </div>
      )}

      {restoreError && (
        <div style={{
          padding:8, border:'1px solid #e57373', borderRadius:4,
          fontSize:12, color:'#c62828', background:'rgba(229,115,115,0.1)',
        }}>
          Error al restaurar: {restoreError}
        </div>
      )}

      {versions.map(v => (
        <div
          key={v.id}
          style={{
            padding:10, border:'1px solid var(--border)', borderRadius:4,
            display:'flex', alignItems:'flex-start', justifyContent:'space-between',
            gap:8,
          }}
        >
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600 }}>
              Versión {v.version_num}
            </div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
              {v.created_at ? new Date(v.created_at).toLocaleString('es-CL') : '—'}
              {v.changed_by_user_id ? ` · usuario #${v.changed_by_user_id}` : ''}
            </div>
            {v.comment && (
              <div style={{ fontSize:11, color:'var(--text-primary)', marginTop:4, fontStyle:'italic' }}>
                "{v.comment}"
              </div>
            )}
            {/* Resumen del snapshot — campos editables relevantes */}
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
              {v.snapshot?.analytics?.length
                ? `Analíticas: ${v.snapshot.analytics.join(', ')}`
                : 'Sin analíticas'}
              {v.snapshot?.loitering_seconds ? ` · merodeo ${v.snapshot.loitering_seconds}s` : ''}
              {v.snapshot?.person_alert ? ' · alerta persona' : ''}
              {v.snapshot?.weapon_detection ? ' · armas' : ''}
              {v.snapshot?.config_extras?.shadow_mode ? ' · calibración' : ''}
              {v.snapshot?.config_extras?.schedule?.mode &&
               v.snapshot.config_extras.schedule.mode !== 'always'
                 ? ` · horario ${v.snapshot.config_extras.schedule.mode}`
                 : ''}
            </div>
          </div>
          <button
            onClick={() => onRestore(v.version_num)}
            disabled={restoring === v.version_num}
            style={{
              padding:'5px 10px', fontSize:11,
              border:'1px solid var(--border)',
              background: restoring === v.version_num ? 'var(--bg-surface)' : '#0091ff',
              color: restoring === v.version_num ? 'var(--text-muted)' : '#fff',
              borderRadius:4,
              cursor: restoring === v.version_num ? 'not-allowed' : 'pointer',
              whiteSpace:'nowrap',
            }}
          >
            {restoring === v.version_num ? 'Restaurando...' : 'Restaurar'}
          </button>
        </div>
      ))}
    </div>
  )
}


// ─── Sprint D — Templates de configuración ────────────────────────────────
// Botón "Guardar como template" + dropdown "Aplicar template" en toolbar.

function TemplateControls({ siteId, token, sites, onApplied }) {
  const [templates, setTemplates] = useState([])
  const [showSave, setShowSave] = useState(false)
  const [showApply, setShowApply] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Save form state
  const [tplName, setTplName] = useState('')
  const [tplDesc, setTplDesc] = useState('')
  const [tplVertical, setTplVertical] = useState('fuel_station')

  const reload = useCallback(async () => {
    if (!token) return
    try {
      const r = await apiFetch(token, '/api/site-config-templates')
      const d = await r.json()
      setTemplates(Array.isArray(d) ? d : [])
    } catch (e) {
      console.warn('templates_load_failed', e)
    }
  }, [token])

  useEffect(() => { reload() }, [reload])

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { setError('Nombre requerido'); return }
    setBusy(true); setError(null)
    try {
      const r = await apiFetch(token, `/api/site-config-templates/from-site/${siteId}`, {
        method: 'POST',
        body: JSON.stringify({
          name: tplName.trim(),
          description: tplDesc.trim() || null,
          vertical: tplVertical || null,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`)
      const tpl = await r.json()
      setShowSave(false)
      setTplName(''); setTplDesc('')
      await reload()
      window.alert(`Template "${tpl.name}" guardado con ${tpl.camera_count} cámaras.`)
    } catch (e) {
      setError(String(e.message || e))
    } finally { setBusy(false) }
  }

  const applyTemplate = async (templateId, templateName) => {
    const siteName = (sites.find(s => s.id === siteId)?.name) || `sitio #${siteId}`
    // Preview: bajar el template + cámaras del sitio destino y armar el diff.
    let previewLine = ''
    try {
      const r1 = await apiFetch(token, `/api/site-config-templates/${templateId}`)
      const tpl = await r1.json()
      const tplChannels = new Set((tpl.cameras_config || []).map(c => c.channel))
      const r2 = await apiFetch(token, `/api/cameras/site/${siteId}`)
      const dstCams = await r2.json()
      const dstChannels = new Set(dstCams.map(c => c.channel))
      const toUpdate = [...tplChannels].filter(ch => dstChannels.has(ch))
      const toCreate = [...tplChannels].filter(ch => !dstChannels.has(ch))
      previewLine = (
        `\nResumen:\n` +
        `  · ${toUpdate.length} cámaras se ACTUALIZARÁN (canales: ${toUpdate.join(', ') || 'ninguno'})\n` +
        `  · ${toCreate.length} cámaras se CREARÁN nuevas (canales: ${toCreate.join(', ') || 'ninguno'})\n`
      )
    } catch (e) {
      previewLine = `\n(No pude calcular el preview: ${e.message})\n`
    }
    const ok = window.confirm(
      `¿Aplicar template "${templateName}" a "${siteName}"?\n` +
      previewLine +
      `\nLas cámaras actualizadas guardan snapshot previo.\n` +
      `Esto NO toca nvr_id, rtsp_url ni posición en plano.`
    )
    if (!ok) return
    setBusy(true); setError(null)
    try {
      const r = await apiFetch(token, `/api/site-config-templates/${templateId}/apply-to/${siteId}`, {
        method: 'POST',
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`)
      const result = await r.json()
      setShowApply(false)
      window.alert(
        `Template aplicado:\n` +
        `  ${result.updated} cámaras actualizadas\n` +
        `  ${result.created} cámaras nuevas creadas\n` +
        (result.skipped?.length ? `  ${result.skipped.length} omitidas (ver logs)` : '')
      )
      onApplied?.()
    } catch (e) {
      setError(String(e.message || e))
    } finally { setBusy(false) }
  }

  return (
    <>
      <button
        onClick={() => setShowSave(true)}
        disabled={!siteId || busy}
        title={tCem("Guardar la configuración de las cámaras de este sitio como un template reusable","Save this site's camera configuration as a reusable template")}
        style={{
          padding:'8px 12px', fontSize:12,
          border:'1px solid var(--border)', borderRadius:4,
          background:'transparent', color:'var(--text-primary)',
          cursor: siteId ? 'pointer' : 'not-allowed',
        }}
      >
        {tCem('Guardar como template','Save as template')}
      </button>
      <button
        onClick={() => { reload(); setShowApply(true) }}
        disabled={!siteId || busy}
        title={tCem("Aplicar un template existente a este sitio","Apply an existing template to this site")}
        style={{
          padding:'8px 12px', fontSize:12,
          border:'1px solid var(--border)', borderRadius:4,
          background:'transparent', color:'var(--text-primary)',
          cursor: siteId ? 'pointer' : 'not-allowed',
        }}
      >
        {tCem('Aplicar template','Apply template')}
      </button>

      {/* Modal SAVE */}
      {showSave && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:12 }}>
              {tCem('Guardar configuración como template','Save configuration as template')}
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10 }}>
              Capturará analytics, thresholds, schedule y modo calibración de
              todas las cámaras habilitadas de este sitio. NO incluye NVR,
              rtsp_url ni posición en el plano.
            </div>
            <label style={modalLabel}>Nombre</label>
            <input
              autoFocus
              value={tplName}
              onChange={e => setTplName(e.target.value)}
              style={modalInput}
              placeholder="ej. Plantilla EDS Copec estándar"
            />
            <label style={modalLabel}>Descripción (opcional)</label>
            <textarea
              value={tplDesc}
              onChange={e => setTplDesc(e.target.value)}
              rows={2}
              style={{ ...modalInput, fontFamily:'inherit' }}
            />
            <label style={modalLabel}>Vertical</label>
            <select
              value={tplVertical}
              onChange={e => setTplVertical(e.target.value)}
              style={modalInput}
            >
              <option value="fuel_station">Combustible (Copec)</option>
              <option value="retail">Retail (Walmart / Hites)</option>
              <option value="office">Oficina</option>
              <option value="">Sin clasificar</option>
            </select>
            {error && <div style={modalError}>{error}</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:14 }}>
              <button onClick={() => setShowSave(false)} disabled={busy} style={modalBtn}>{tCem("Cancelar","Cancel")}</button>
              <button onClick={saveAsTemplate} disabled={busy} style={{ ...modalBtn, background:'#0091ff', color:'#fff', border:'none' }}>
                {busy ? tCem('Guardando...','Saving...') : tCem('Guardar','Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal APPLY */}
      {showApply && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:12 }}>
              {tCem('Aplicar template','Apply template')} a este sitio
            </div>
            {templates.length === 0 && (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:14, textAlign:'center' }}>
                {tCem('Aún no hay templates guardados. Usá "Guardar como template" primero.','No templates saved yet. Use "Save as template" first.')}
              </div>
            )}
            <div style={{ maxHeight:300, overflowY:'auto' }}>
              {templates.map(t => (
                <div
                  key={t.id}
                  style={{
                    padding:10, border:'1px solid var(--border)', borderRadius:4,
                    marginBottom:8,
                    display:'flex', justifyContent:'space-between', alignItems:'center', gap:8,
                  }}
                >
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                      {t.camera_count} cámaras · {t.vertical || 'sin clasificar'}
                      {t.source_site_id ? ` · de sitio #${t.source_site_id}` : ''}
                    </div>
                    {t.description && (
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, fontStyle:'italic' }}>
                        "{t.description}"
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <button
                      onClick={() => applyTemplate(t.id, t.name)}
                      disabled={busy}
                      style={{
                        padding:'5px 10px', fontSize:11,
                        border:'none', background:'#0091ff', color:'#fff',
                        borderRadius:4, cursor:'pointer', whiteSpace:'nowrap',
                      }}
                    >
                      {tCem('Aplicar','Apply')}
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`¿Eliminar template "${t.name}"?\nQuedará archivado (soft-delete), no se borra.`)) return
                        setBusy(true)
                        try {
                          const r = await apiFetch(token, `/api/site-config-templates/${t.id}`, { method:'DELETE' })
                          if (!r.ok) throw new Error(`HTTP ${r.status}`)
                          await reload()
                        } catch (e) { setError(String(e.message || e)) }
                        finally { setBusy(false) }
                      }}
                      disabled={busy}
                      style={{
                        padding:'4px 8px', fontSize:10,
                        border:'1px solid var(--border)',
                        background:'transparent', color:'var(--text-muted)',
                        borderRadius:4, cursor:'pointer', whiteSpace:'nowrap',
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {error && <div style={modalError}>{error}</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:14 }}>
              <button onClick={() => setShowApply(false)} disabled={busy} style={modalBtn}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const modalOverlay = {
  position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
  display:'flex', alignItems:'center', justifyContent:'center',
  zIndex:100,
}
const modalBox = {
  background:'var(--bg-surface)', padding:20, borderRadius:6,
  border:'1px solid var(--border)', minWidth:380, maxWidth:520,
  maxHeight:'90vh', overflowY:'auto',
}
const modalLabel = {
  display:'block', fontSize:11, color:'var(--text-muted)',
  marginTop:8, marginBottom:4,
}
const modalInput = {
  width:'100%', padding:'6px 8px', fontSize:13,
  background:'var(--bg-elev-1)', color:'var(--text-primary)',
  border:'1px solid var(--border)', borderRadius:4,
  boxSizing:'border-box',
}
const modalBtn = {
  padding:'6px 12px', fontSize:12,
  border:'1px solid var(--border)', borderRadius:4,
  background:'transparent', color:'var(--text-primary)',
  cursor:'pointer',
}
const modalError = {
  marginTop:10, padding:8, fontSize:12,
  background:'rgba(217,48,37,0.1)', border:'1px solid #d93025',
  color:'#d93025', borderRadius:4,
}


// ─── Sprint C — Vista Salud Configurador ──────────────────────────────────
// Cards de KPIs arriba del plano. Refresca cada 30s. Accesibilidad:
// cifras + etiquetas textuales, NO depender de color.

function ConfigHealthBar({ siteId, token, cameras }) {
  const [data, setData]   = useState(null)
  const [error, setError] = useState(null)

  const reload = useCallback(async () => {
    if (!siteId || !token) return
    try {
      const r = await apiFetch(token, `/api/sites/${siteId}/config_health`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = await r.json()
      setData(j); setError(null)
    } catch (e) {
      setError(String(e.message || e))
    }
  }, [siteId, token])

  // Initial + cada 30s + cuando cameras cambian (cambio local optimista)
  useEffect(() => { reload() }, [reload, cameras.length])
  useEffect(() => {
    if (!siteId) return
    const id = setInterval(reload, 30000)
    return () => clearInterval(id)
  }, [siteId, reload])

  if (error) {
    return (
      <div style={{
        padding:'8px 10px', fontSize:11,
        border:'1px solid var(--border)', borderRadius:4,
        color:'var(--text-muted)', marginBottom:10,
      }}>
        Salud configurador no disponible: {error}
      </div>
    )
  }
  if (!data) {
    return (
      <div style={{
        padding:'8px 10px', fontSize:11, color:'var(--text-muted)',
        marginBottom:10,
      }}>
        Cargando salud del configurador...
      </div>
    )
  }

  const s = data.summary
  const kpis = [
    { label:'Cámaras',         value: s.total,         hint: `${s.active} activas · ${s.disabled} deshabilitadas` },
    { label:'Activas ahora',   value: s.active_now,    hint: 'Generando alertas en este momento' },
    { label:'Silenciadas hoy', value: s.silenced_now,  hint: 'Por horario configurado · pasarán a activas según schedule' },
    { label:'En calibración',  value: s.in_shadow,     hint: 'Capturando shadow_alerts sin generar incidentes' },
    { label:'Con horario',     value: s.scheduled,     hint: 'Tienen schedule preset o personalizado' },
    { label:'Cambios hoy',     value: s.versions_today,hint: `${s.versions_week} esta semana` },
  ]

  // Última modificación textual
  let lastTxt = 'Sin cambios registrados aún.'
  if (data.last_change?.at) {
    const when = new Date(data.last_change.at).toLocaleString('es-CL')
    const camId = data.last_change.camera_id
    const cam = cameras.find(c => c.id === camId)
    const camTxt = cam ? `CH${String(cam.channel).padStart(2,'0')} ${cam.name}` : `cámara #${camId}`
    const byUser = data.last_change.user_id ? ` por usuario #${data.last_change.user_id}` : ''
    lastTxt = `Último cambio: ${when}${byUser} · ${camTxt}`
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',
        gap:8,
      }}>
        {kpis.map(k => (
          <div
            key={k.label}
            title={k.hint}
            style={{
              padding:'8px 10px',
              border:'1px solid var(--border)',
              borderRadius:4,
              background:'var(--bg-elev-1)',
            }}
          >
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{k.label}</div>
            <div style={{ fontSize:20, fontWeight:600, marginTop:2 }}>{k.value}</div>
            <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
              {k.hint}
            </div>
          </div>
        ))}
      </div>
      <div style={{
        fontSize:11, color:'var(--text-muted)', marginTop:6,
      }}>
        {lastTxt}
      </div>
    </div>
  )
}


// ─── Vista principal ──────────────────────────────────────────────────────

// Clave localStorage para recordar el sitio entre sesiones
const LS_KEY_SITE = 'retaliq.siteConfig.lastSiteId'

export default function SiteConfigView() {
  const token = useAuthStore(s => s.token)
  const [sites, setSites]       = useState([])
  // Restaurar el último sitio elegido (sobrevive F5 y cierre de tab)
  const [siteId, setSiteIdRaw] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY_SITE)
      return v ? Number(v) : null
    } catch { return null }
  })
  const setSiteId = useCallback((id) => {
    setSiteIdRaw(id)
    try { localStorage.setItem(LS_KEY_SITE, String(id)) } catch {}
  }, [])
  const [cameras, setCameras]   = useState([])
  const [planMeta, setPlanMeta] = useState(null)
  const [imgBlob, setImgBlob]   = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  // Click-to-place: id de cámara "armada" para el próximo click sobre el mapa
  const [placingCameraId, setPlacingCameraId] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading]   = useState(true)
  const [err, setErr]           = useState(null)
  const viewportRef = useRef(null)
  const fileInputRef = useRef(null)

  // Cargar lista de sitios al inicio
  useEffect(() => {
    if (!token) return
    apiFetch(token, '/api/sites/?limit=100')
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.items || [])
        setSites(list)
        // Si el siteId guardado en localStorage ya no existe (sitio eliminado),
        // resetear al primero disponible
        const validIds = new Set(list.map(s => s.id))
        if (list.length && (!siteId || !validIds.has(siteId))) {
          setSiteId(list[0].id)
        }
      })
      .catch(e => setErr(e.message))
  }, [token])

  // Cargar cámaras + plano del sitio seleccionado
  const reload = useCallback(async () => {
    if (!token || !siteId) return
    setLoading(true); setErr(null); setImgBlob(null); setPlanMeta(null)
    try {
      const cams = await apiFetch(token, `/api/cameras/site/${siteId}`).then(r => r.json())
      setCameras(cams)
      try {
        const meta = await apiFetch(token, `/api/site-floor-plans/${siteId}`).then(r => r.json())
        setPlanMeta(meta)
        const blob = await apiFetch(token, `/api/site-floor-plans/${siteId}/image`).then(r => r.blob())
        setImgBlob(URL.createObjectURL(blob))
      } catch (e) {
        // 404 = sin plano, OK
        if (!/HTTP 404/.test(e.message)) throw e
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [token, siteId])

  useEffect(() => { reload() }, [reload])

  const selectedCam = useMemo(
    () => cameras.find(c => c.id === selectedId) || null,
    [cameras, selectedId],
  )

  // Mover cámara → POST position + actualizar lista local
  const moveCamera = async (camId, x_pct, y_pct) => {
    const cam = cameras.find(c => c.id === camId)
    if (!cam) return
    const body = JSON.stringify({
      x_pct: Math.max(0, Math.min(1, x_pct)),
      y_pct: Math.max(0, Math.min(1, y_pct)),
      rotation_deg: cam.floor_plan?.rotation_deg || 0,
    })
    try {
      const updated = await apiFetch(token, `/api/cameras/${camId}/position`, {
        method: 'POST', body,
      }).then(r => r.json())
      setCameras(prev => prev.map(c => c.id === camId ? updated : c))
    } catch (e) {
      setErr(`Error moviendo cámara: ${e.message}`)
    }
  }

  // Colocar cámara sin posicionar en el centro del plano
  const placeAtCenter = (camId) => moveCamera(camId, 0.5, 0.5)

  // Actualizar campos cámara → PUT
  const updateCamera = async (camId, changes) => {
    const updated = await apiFetch(token, `/api/cameras/${camId}`, {
      method: 'PUT',
      body: JSON.stringify(changes),
    }).then(r => r.json())
    setCameras(prev => prev.map(c => c.id === camId ? updated : c))
  }

  // Upload de plano
  const uploadPlan = async (file) => {
    if (!file) return
    setUploading(true); setErr(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('label', `v${(planMeta?.id || 0) + 1} ${file.name}`)
      await apiFetch(token, `/api/site-floor-plans/${siteId}/upload`, {
        method: 'POST', body: fd,
      })
      await reload()
    } catch (e) {
      setErr(`Error subiendo plano: ${e.message}`)
    } finally {
      setUploading(false)
    }
  }

  if (!token) return <div style={{ padding: 24 }}>No autenticado</div>

  const placed = cameras.filter(c => c.floor_plan?.x_pct != null).length

  return (
    <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sitio</div>
          <select
            value={siteId || ''}
            onChange={e => { setSiteId(Number(e.target.value)); setSelectedId(null) }}
            style={{
              padding: '6px 10px', fontSize: 13,
              background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', borderRadius: 4,
              minWidth: 200,
            }}
          >
            {sites.map(s => <option key={s.id} value={s.id}>{s.name || s.code}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {cameras.length} cámaras · {placed} posicionadas · {planMeta ? 'plano cargado' : 'sin plano'}
        </div>
        {/* Sprint D — Templates */}
        <TemplateControls
          siteId={siteId}
          token={token}
          sites={sites}
          onApplied={reload}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!siteId || uploading}
          style={{
            padding: '8px 14px', fontSize: 12,
            border: '1px solid var(--border)', borderRadius: 4,
            background: 'transparent', color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          {uploading ? 'Subiendo...' : (planMeta ? 'Reemplazar plano' : 'Subir plano')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,application/pdf"
          onChange={e => uploadPlan(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
      </div>

      {/* Sprint C — Salud del Configurador */}
      {siteId && (
        <ConfigHealthBar siteId={siteId} token={token} cameras={cameras} />
      )}

      {err && (
        <div style={{
          padding: 8, fontSize: 12,
          background: 'rgba(217,48,37,0.1)', border: '1px solid #d93025',
          color: '#d93025', borderRadius: 4, marginBottom: 12,
        }}>
          {err}
        </div>
      )}

      {/* Plano georreferenciado sobre mapa OSM (sprint planograma 2026-06-12).
          Reemplaza al FloorPlanCanvas Konva viejo. Maneja multinivel,
          georreferenciación obligatoria, buscador Nominatim, recálculo de
          lat/lon de cámaras al ajustar el plano. */}
      <div ref={viewportRef} style={{ flex: 1, minHeight: 480 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            Cargando configuración del sitio...
          </div>
        ) : (
          <SiteFloorPlanSection
            siteId={siteId}
            token={token}
            cameras={cameras}
            selectedCameraId={selectedId}
            onSelectCamera={setSelectedId}
            onCamerasChanged={reload}
            placingCameraId={placingCameraId}
            onPlacingCameraChange={setPlacingCameraId}
            height={560}
          />
        )}
        <CamerasStrip
          cameras={cameras}
          selectedId={selectedId}
          onSelectCamera={(id) => {
            setSelectedId(id)
            setPlacingCameraId(null)
          }}
          placingCameraId={placingCameraId}
          onArmPlacement={(id) => setPlacingCameraId(id)}
          onClearPlacement={() => setPlacingCameraId(null)}
        />
      </div>

      {/* Drawer lateral */}
      {selectedCam && (
        <CameraDrawer
          camera={selectedCam}
          siteId={siteId}
          onClose={() => setSelectedId(null)}
          onUpdate={updateCamera}
          token={token}
          onUpdatePosition={async (camId, pos) => {
            const updated = await apiFetch(token, `/api/cameras/${camId}/position`, {
              method: 'POST',
              body: JSON.stringify(pos),
            }).then(r => r.json())
            setCameras(prev => prev.map(c => c.id === camId ? updated : c))
          }}
          onLivePreview={(camId, patch) => {
            // Actualiza solo state local — sin pegarle al backend —
            // así el cono en el plano se redibuja al instante mientras
            // el operador mueve los sliders. El POST real ocurre en Save.
            setCameras(prev => prev.map(c => c.id === camId ? { ...c, ...patch } : c))
          }}
        />
      )}
    </div>
  )
}